import * as React from "react";
import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";

// react-bootstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Form,
} from "react-bootstrap";

import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { AuthContext } from "contexts/authContext";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import { getFirewallLogs } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import _ from "lodash";
import moment from "moment";
import { useTour } from "@reactour/tour";
import { tourGuideSteps } from "views/Reports/ReportsSteps";

function FirewallLogList(props) {
  // Contexts
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const { clientId, clientEnvironmentId } = useParams();
  const { clientName, clientEnvironmentName } = props.location.state;

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  // States
  const [tableData, setTableData] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const headers = [
    { key: "timestamp", label: "Timestamp" },
    { key: "reason", label: "Triggered Rules" },
    { key: "http_method", label: "Method" },
    { key: "http_url", label: "URI" },
    { key: "ip", label: "IP" },
    { key: "country", label: "Country" },
  ];

  // Pagination Starts
  const columnMapping = {
    Timestamp: "timestamp",
    "Triggered Rules": "reason",
    Method: "http_method",
    URI: "http_url",
    IP: "ip",
    Country: "country",
  };

  const [totalCount, setTotalCount] = useState(0);
  const numberOfRowsData = [25, 50, 100, 500, 1000];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("DESC");
  const [sortValue, setSortValue] = useState("Timestamp");
  const [showDownloadGridResults, setShowDownloadGridResults] = useState(true);

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const handleEndDateChange = (newEndDate) => {
    if (newEndDate) {
      const endDateWithTime = moment(newEndDate)
        .endOf("day")
        .format("MM/DD/YYYY h:mm A");
      setSelectedEndDate(endDateWithTime);
    } else {
      setSelectedEndDate(newEndDate);
    }
  };

  const fetchFirewallLogs = async (variables = {}, isCSVDownload = false) => {
    try {
      // Validate dates are selected
      if (!selectedStartDate || !selectedEndDate) {
        if (!isCSVDownload) {
          notify("danger", "Please select both start and end dates");
        }
        return false;
      }

      setShowLoader(true);
      const params = {
        start_date: moment(selectedStartDate).utc().format(),
        end_date: moment(selectedEndDate).utc().format(),
        client_environment_id: clientEnvironmentId,
        limit: variables.limit || currentLimit,
      };

      const response = await API.graphql(
        graphqlOperation(getFirewallLogs, params)
      );

      if (response.data?.getFirewallLogs?.success) {
        setShowDownloadGridResults(true);
        const logsData = response.data.getFirewallLogs.body.acl_logs || [];
        if (!isCSVDownload) {
          setTableData(logsData);
          setTotalCount(logsData.length);
          notify("success");
        } else {
          const filter = {
            "Start Date": params.start_date,
            "End Date": params.end_date,
          };
          const messageDataForCSV = logsData;
          return { messageDataForCSV, filter };
        }
        return true;
      } else {
        console.error(response.data?.getFirewallLogs?.message);
        notify("danger", response.data?.getFirewallLogs?.message || "Failed to fetch firewall logs");
        return false;
      }
    } catch (error) {
      console.error(error);
      notify("danger", "An error occurred while fetching firewall logs");
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const ellipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  if (showLoader) return <Spinner animation="grow" />;

  if (errorMessage) return errorMessage;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Firewall Logs</h2>
          </Col>
          <Col className="my-auto text-right">
            {currentUser.groupName === "external" && (
              <Button
                variant="secondary"
                className="btn-outline ml-4"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )}
          </Col>
        </Row>
        <Row>
          <Col md="12" sm="12" id="environment-details">
            <h5>Client name: {clientName}</h5>
            <h5>Environment Name: {clientEnvironmentName}</h5>
            <h5>Environment Id: {clientEnvironmentId}</h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card id="firewall-logs-filter">
              <Card.Body>
                <Row>
                  <Col md="4">
                    <Form.Group>
                      <label>Start Date</label>
                      <CustomReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Not Selected",
                          value: selectedStartDate
                            ? moment(selectedStartDate).format(
                                "MM/DD/YYYY h:mm A"
                              )
                            : "",
                        }}
                        timeFormat={true}
                        value={selectedStartDate}
                        onChange={(value) => {
                          setSelectedStartDate(value);
                        }}
                      ></CustomReactDatetime>
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group>
                      <label>End Date</label>
                      <CustomReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Not Selected",
                          value: selectedEndDate
                            ? moment(selectedEndDate).format(
                                "MM/DD/YYYY h:mm A"
                              )
                            : "",
                        }}
                        timeFormat={true}
                        value={selectedEndDate}
                        onChange={(value) => {
                          handleEndDateChange(value);
                        }}
                      ></CustomReactDatetime>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      variant="primary"
                      onClick={() => {
                        // Validate start date is not after end date
                        if (
                          selectedStartDate &&
                          selectedEndDate &&
                          moment(selectedStartDate).isAfter(
                            moment(selectedEndDate)
                          )
                        ) {
                          notify(
                            "danger",
                            "Start date must be lesser than end date"
                          );
                          return;
                        }
                        setCurrentOffset(0);
                        fetchFirewallLogs({
                          offSet: 0,
                          limit: currentLimit,
                          sortType: ascOrDescValue,
                          sortFilter: columnMapping[sortValue],
                        });
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="default"
                      className="btn-outline"
                      style={{ marginLeft: "30px" }}
                      onClick={() => {
                        setSelectedStartDate(null);
                        setSelectedEndDate(null);
                        setShowDownloadGridResults(false);
                      }}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <ReactTable
              id="firewall-logs-data-grid"
              data={tableData}
              totalCount={totalCount}
              functionCallBack={fetchFirewallLogs}
              setStateForPagnination={setStateForPagnination}
              currentLimit={currentLimit}
              currentOffset={currentOffset}
              ascOrDescValue={ascOrDescValue}
              sortValue={sortValue}
              numberOfRowsData={numberOfRowsData}
              columnMapping={columnMapping}
              tableName="Firewall Logs"
              CSVHeaders={headers}
              CSVFileName="Firewall_Logs_Export"
              notify={notify}
              showDownloadGridResults={showDownloadGridResults}
              columns={[
                {
                  Header: "Timestamp",
                  Cell: (cell) => {
                    const row = cell.row.original;
                    const date = new Date(row.timestamp).toLocaleString();
                    return <div>{date}</div>;
                  },
                  accessor: "timestamp",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 180,
                },
                {
                  Header: "Triggered Rules",
                  accessor: "reason",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 250,
                },
                {
                  Header: "Method",
                  accessor: "http_method",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 100,
                },
                {
                  Header: "URI",
                  accessor: "http_url",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 200,
                },
                {
                  Header: "IP",
                  accessor: "ip",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 150,
                },
                {
                  Header: "Country",
                  accessor: "country",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 100,
                },
              ]}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default FirewallLogList;
